import { useMutation } from '@tanstack/react-query'
import { useContext, useEffect, useRef } from 'react'
import { DictType } from '@pma/types'
import { cn, getUserFromId, formatDate, formatDate_he, formatTime } from '@pma/utils/helpers'

import { Button } from '@components/common/button'
import { Ellipsis, Trash2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/common/tooltip'
import { useAuth } from '@stores/auth-store'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { USERS_PAGE_ROUTE } from '@pma/utils/page-routes'
import { Separator } from '@components/common/separator'
import useTranslator from '@hooks/use-translator'
import { deleteTaskSession, getTotalTime } from '@pma/utils/utils'
import { queryClient } from '@pma/utils/constants'
import { GroupedTasksContext } from './grouped-tasks/context/GroupedTasksProvider';

const TaskSessions = ({
  activeTime,
  sessions,
  parentTaskId
}: {
  activeTime: string | undefined
  sessions: Array<DictType>
  parentTaskId?: number;
}) => {
  const { translate } = useTranslator()
  const navigate = useNavigate()

  const ref = useRef<any>(undefined)
  const [searchParam] = useSearchParams();

  const newParams = new URLSearchParams(searchParam);
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext);

  const inactiveSessions = sessions?.filter((session) => !session.in_progress) || []

  const element = ref?.current?.getElement()

  useEffect(() => {
    if (typeof element !== 'undefined') {

      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
    }
  }, [element, inactiveSessions?.length])
  return (
    <div>
      <Ellipsis
        onClick={(e) => {
          newParams.set('revalidate', 'tasks');
          navigate(`?${newParams.toString()}`);
          queryClient.invalidateQueries({queryKey:['grouped-tasks']})
          if (parentTaskId) {
            setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
          }
          ref?.current?.toggle(e)
        }}
      />

      <div>
        {activeTime && (
          <div className='my-2 flex flex-col items-center'>
            <h6 className='font-medium'>{translate('Currently active session')}</h6>
            <span className='text-3xl'>{activeTime && activeTime}</span>
          </div>
        )}
        {sessions?.length > 0 && (
          <div className=''>
            {sessions
              ?.sort((a, b) => b.start - a.start)
              ?.map((session, index) => <Session session={session} key={index} parentTaskId={parentTaskId} />)}
          </div>
        )}
        {inactiveSessions?.length < 1 && !activeTime && (
          <i
            style={{
              fontSize: '0.9rem',
            }}
          >
            {translate('No Available Session')}
          </i>
        )}
      </div>
    </div>
  )
}

export default TaskSessions

const Session = ({ session, parentTaskId }: { session: DictType, parentTaskId?: number; }) => {
  const { authConfig } = useAuth()
  const { locale } = useTranslator()
  const navigate = useNavigate()

  const [searchParam] = useSearchParams();

  const newParams = new URLSearchParams(searchParam);
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext);
  const isHebrew = locale === 'he'

  const organizationUsers = authConfig?.organizationUsers

  let userId = authConfig?.id

  const deleteTaskSessionMutation = useMutation({
    mutationKey: ['delete-task-session', session.id, userId],
    mutationFn: deleteTaskSession,
    onSuccess: (data) => {
      // revalidate task information
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      }
    },
    onError: (error) => {
      // revalidate task information
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      }
    },
  })
  // delete task session
  const handleDelete = async () => {
    deleteTaskSessionMutation.mutate(session.id)
  }

  const responsibleUser = getUserFromId(session?.user_id, organizationUsers)

  return (
    <div className='my-2'>
      <div className={cn('w-full justify-between gap-2', session.in_progress ? 'hidden' : 'flex')}>
        <Tooltip>
          <TooltipTrigger>
            <Link to={USERS_PAGE_ROUTE + `/${session?.user_id}`}>
              <img
                alt=''
                src={
                  responsibleUser?.user_picture?.url
                    ? responsibleUser?.user_picture.url
                    : `${process.env.PUBLIC_URL}/ionaddoutline@2x.png`
                }
                className='h-6 w-6 rounded-full'
              />
            </Link>
          </TooltipTrigger>
          <TooltipContent>{responsibleUser?.name}</TooltipContent>
        </Tooltip>

        <small>
          {isHebrew
            ? formatDate_he(session.created_at, {
                day: 'numeric',
                month: 'short',
              })
            : formatDate(session.created_at, undefined, {
                day: 'numeric',
                month: 'short',
              })}
        </small>
        <div className='flex flex-1 justify-start pl-2 text-sm'>
          <small>{formatTime(session.start, { amPm: true })}</small>
          <small
            style={{
              marginInline: '5px',
            }}
          >
            -
          </small>
          <small>{formatTime(session.end, { amPm: true })}</small>
        </div>
        <small>{formatTime(getTotalTime([session]), {})}</small>

        <Button
          onClick={handleDelete}
          disabled={deleteTaskSessionMutation.isPending}
          loading={deleteTaskSessionMutation.isPending}
          className='h-4 w-4'
          size={'icon'}
          variant={'ghost'}
        >
          <Trash2 className='text-red-500' size={12} />
        </Button>
      </div>
      <Separator />
    </div>
  )
}
